import { DataListComponent } from '../../../shared/ui/data-list/data-list.component';
import { DataPage, DataQuery } from '../../../models/data-source';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, of } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import {
  DataTableColumn, DataTableComponent,
} from '../../../shared/ui/data-table/data-table.component';
import {
  DataFilterField, DataFilterOption, DataFilterValues,
} from '../../../shared/ui/data-filter/data-filter.component';
import { toRuleParams } from '../../../shared/ui/data-filter/data-filter.rules';
import { PagedDto } from '../../../models/paged.dto';
import {
  CONTRACT_BILLING_METHOD_LABELS, CONTRACT_MODIFICATION_LABELS, optionsOf,
} from './contract-options';
import {
  ContractDocumentsData, ContractDocumentsDialogComponent,
} from './contract-documents-dialog.component';

/** One row of `/api/contracts`. Mirrors ContractListDto. */
export interface ContractRow {
  id: number;
  customerId: number | null;
  customerName: string | null;
  leadId: number | null;
  leadName: string | null;
  rollOffOpportunityId: number | null;
  rollOffSiteName: string | null;
  transactionCodeShortDescription: string | null;
  price: number;
  entryDate: string;
  effectiveFrom: string;
  effectiveTo: string;
  contractStatusCode: string;
  contractStatusLabel: string;
  title: string | null;
  contractNo: string | null;
  poReleaseNumber: string | null;
  sentAt: string | null;
  signedOn: string | null;
  signedBy: string | null;
  activatedAt: string | null;
  terminatedAt: string | null;
  terminatedReason: string | null;
  billingMethod: string;
  billingDayOfMonth: number;
  expectedMonthlyCount: number | null;
  skipReducesBill: boolean;
  totalLoads: number;
  totalPickups: number;
  totalVolume: number;
  emailScaleTicketClose: boolean;
  emailServiceOrderClose: boolean;
  allowedModificationTypes: string[];
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

interface ContractStatusLookup { id: number; code: string; label: string; }

/**
 * The Status control's opening value: the two statuses that still need
 * somebody - a Draft to send, and a Sent contract awaiting its signature. Not
 * a status of its own; it is sent as both.
 */
export const AWAITING_SIGNATURE = 'awaiting';

/** The end date the contract forms write for "No expiry date". */
const NO_EXPIRY = '9999-12-31';

@Component({
  selector: 'app-pending-contracts',
  templateUrl: './pending-contracts.component.html',
})
export class PendingContractsComponent implements OnInit, OnDestroy {
  @ViewChild('compactList') compactList?: DataListComponent<ContractRow>;
  collectionSearch = '';

  /**
   * Every field a contract carries that means something to the business, in
   * the order a contract is lived: who and what, the money, the dates, the
   * signing, billing, the service counters, then who entered it.
   *
   * A column id is also its server sort key and its filter rule's field, so
   * one id names the same thing in the header, the sort and the filter.
   * Left out on purpose: internal ids, the tenant, a manual sort order, the
   * terms text and the signed document (the customer page's contract card
   * shows those), and the note from the retired revision workflow.
   */
  readonly columns: DataTableColumn[] = [
    { id: 'contractNo', label: 'Contract #' },
    { id: 'title', label: 'Contract Title' },
    { id: 'customer', label: 'Customer' },
    { id: 'tc', label: 'Transaction Code' },
    { id: 'price', label: 'Price' },
    { id: 'poReleaseNumber', label: 'PO Release #' },
    { id: 'entryDate', label: 'Entered' },
    { id: 'status', label: 'Status' },
    { id: 'effectiveFrom', label: 'Effective From' },
    { id: 'effectiveTo', label: 'Effective To' },
    { id: 'sentAt', label: 'Sent' },
    { id: 'signedOn', label: 'Signed' },
    { id: 'signedBy', label: 'Signed By' },
    { id: 'activatedAt', label: 'Activated' },
    { id: 'terminatedAt', label: 'Terminated' },
    { id: 'terminatedReason', label: 'Termination Reason' },
    { id: 'billingMethod', label: 'Billing Method' },
    { id: 'billingDayOfMonth', label: 'Billing Day of Month' },
    { id: 'expectedMonthlyCount', label: 'Expected Monthly Count' },
    { id: 'skipReducesBill', label: 'Skip Reduces Bill' },
    { id: 'totalLoads', label: 'Total Loads' },
    { id: 'totalPickups', label: 'Total Pickups' },
    { id: 'totalVolume', label: 'Total Volume' },
    { id: 'emailScaleTicketClose', label: 'Email on Scale Ticket Close' },
    { id: 'emailServiceOrderClose', label: 'Email on Service Order Close' },
    // A set, not a value: there is no order a person would mean by it.
    { id: 'allowedModificationTypes', label: 'Self-Service Modifications', sortable: false },
    { id: 'createdAt', label: 'Created' },
    { id: 'createdBy', label: 'Created By' },
    { id: 'updatedAt', label: 'Updated' },
    { id: 'updatedBy', label: 'Updated By' },
  ];

  /** The five columns the page has always opened on; the rest are a tick away in the picker. */
  private static readonly OpeningColumns: readonly string[] =
    ['customer', 'tc', 'price', 'entryDate', 'status'];

  readonly defaultHiddenColumns: readonly string[] = this.columns
    .map(column => column.id)
    .filter(id => !PendingContractsComponent.OpeningColumns.includes(id));

  statuses: ContractStatusLookup[] = [];
  filterFields: DataFilterField[] = [];
  /** The rule half of the bar: every field but Status, which has its own parameter. */
  private ruleFields: DataFilterField[] = [];
  /** Opens on the queue this page was built as. */
  filterValues: DataFilterValues = { status: AWAITING_SIGNATURE };

  @ViewChild('table') table?: DataTableComponent<ContractRow>;

  private readonly destroyed$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private router: Router,
    private dialog: MatDialog,
  ) {
    this.buildFilterFields();
  }

  ngOnInit(): void {
    // The statuses behind the Status control. A failed read leaves only
    // "Draft or Sent" and "Any status", which still reach every contract.
    this.http.get<ContractStatusLookup[]>('/api/lookups/contract-statuses').pipe(
      catchError(() => of([] as ContractStatusLookup[])),
      takeUntil(this.destroyed$),
    ).subscribe(statuses => {
      this.statuses = statuses;
      this.buildFilterFields();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Rebuilt when the status lookup arrives rather than computed in a getter:
   * the template reads this every change-detection pass, and a fresh array
   * each time would re-enter the filter bar's ngOnChanges for nothing.
   */
  private buildFilterFields(): void {
    const none: readonly DataFilterOption[] = [];
    const yesNo: readonly DataFilterOption[] = [
      { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' },
    ];
    this.ruleFields = [
      { id: 'contractNo', label: 'Contract #', kind: 'text', pinned: false, options: none },
      { id: 'title', label: 'Contract Title', kind: 'text', pinned: false, options: none },
      { id: 'customer', label: 'Customer', kind: 'text', pinned: false, options: none },
      { id: 'tc', label: 'Transaction Code', kind: 'text', pinned: false, options: none },
      { id: 'price', label: 'Price', kind: 'number', pinned: false, options: none },
      { id: 'poReleaseNumber', label: 'PO Release #', kind: 'text', pinned: false, options: none },
      { id: 'entryDate', label: 'Entered', kind: 'date', pinned: false, options: none },
      { id: 'effectiveFrom', label: 'Effective From', kind: 'date', pinned: false, options: none },
      { id: 'effectiveTo', label: 'Effective To', kind: 'date', pinned: false, options: none },
      { id: 'sentAt', label: 'Sent', kind: 'date', pinned: false, options: none },
      { id: 'signedOn', label: 'Signed', kind: 'date', pinned: false, options: none },
      { id: 'signedBy', label: 'Signed By', kind: 'text', pinned: false, options: none },
      { id: 'activatedAt', label: 'Activated', kind: 'date', pinned: false, options: none },
      { id: 'terminatedAt', label: 'Terminated', kind: 'date', pinned: false, options: none },
      { id: 'terminatedReason', label: 'Termination Reason', kind: 'text', pinned: false, options: none },
      {
        id: 'billingMethod', label: 'Billing Method', kind: 'choice', pinned: false,
        allLabel: 'Any billing method', options: optionsOf(CONTRACT_BILLING_METHOD_LABELS),
      },
      { id: 'billingDayOfMonth', label: 'Billing Day of Month', kind: 'number', pinned: false, options: none },
      { id: 'expectedMonthlyCount', label: 'Expected Monthly Count', kind: 'number', pinned: false, options: none },
      { id: 'skipReducesBill', label: 'Skip Reduces Bill', kind: 'choice', pinned: false, options: yesNo },
      { id: 'totalLoads', label: 'Total Loads', kind: 'number', pinned: false, options: none },
      { id: 'totalPickups', label: 'Total Pickups', kind: 'number', pinned: false, options: none },
      { id: 'totalVolume', label: 'Total Volume', kind: 'number', pinned: false, options: none },
      {
        id: 'emailScaleTicketClose', label: 'Email on Scale Ticket Close', kind: 'choice',
        pinned: false, options: yesNo,
      },
      {
        id: 'emailServiceOrderClose', label: 'Email on Service Order Close', kind: 'choice',
        pinned: false, options: yesNo,
      },
      {
        id: 'allowedModificationTypes', label: 'Self-Service Modifications', kind: 'choice',
        pinned: false, allLabel: 'Any', options: optionsOf(CONTRACT_MODIFICATION_LABELS),
      },
      { id: 'createdAt', label: 'Created', kind: 'date', pinned: false, options: none },
      { id: 'createdBy', label: 'Created By', kind: 'text', pinned: false, options: none },
      { id: 'updatedAt', label: 'Updated', kind: 'date', pinned: false, options: none },
      { id: 'updatedBy', label: 'Updated By', kind: 'text', pinned: false, options: none },
    ];
    this.filterFields = [
      // Pinned, and not a rule: status has its own `statuses` parameter, which
      // takes Draft and Sent together - the value this page opens on.
      {
        id: 'status', label: 'Status', allLabel: 'Any status', pinned: true,
        options: [
          { value: AWAITING_SIGNATURE, label: 'Draft or Sent' },
          ...this.statuses.map(status => ({ value: status.code, label: status.label })),
        ],
      },
      ...this.ruleFields,
    ];
  }

  /** The filter bar applied its values; the table re-reads from page 1. */
  applyFilters(values: DataFilterValues): void {
    this.filterValues = values;
  }

  /** Server-mode source: one request for the page, its statuses and its rules. */
  fetch = (q: DataQuery): Observable<DataPage<ContractRow>> => {
    let params = new HttpParams()
      .set('page', String(q.page))
      .set('pageSize', String(q.pageSize));
    // Repeated parameters, which is what binds to an array server-side.
    for (const status of this.statusParams(q.filters)) params = params.append('statuses', status);
    for (const rule of toRuleParams(q.filters, this.ruleFields)) params = params.append('rule', rule);
    if (q.search) params = params.set('search', q.search);
    if (q.sort) params = params.set('sort', q.sort).set('desc', String(q.desc ?? false));

    return this.http.get<PagedDto<ContractRow>>('/api/contracts', { params })
      .pipe(map(page => ({ items: page.items, total: page.total })));
  };

  /**
   * The Status control as the `statuses` the server reads: both awaiting
   * statuses, the one picked, or none - which the server reads as every
   * status. "Any status" used to be unreachable: clearing both checkboxes
   * showed nothing, so an Active contract could not be found on this page.
   */
  statusParams(filters: DataFilterValues | undefined): string[] {
    const held = filters?.['status'];
    if (held === AWAITING_SIGNATURE) return ['Draft', 'Sent'];
    return typeof held === 'string' && held ? [held] : [];
  }

  load(): void {
    this.table?.reload();
    this.compactList?.reload();
  }

  /**
   * Who the contract is with. A contract made from an accepted quote belongs
   * to its lead until signing makes that lead a customer, and a roll-off
   * contract to its hire - so the name comes from whichever it has, rather
   * than printing "Customer #null".
   */
  ownerName(row: ContractRow): string | null {
    return row.customerName ?? row.leadName ?? row.rollOffSiteName ?? null;
  }

  /** Said beside the name when the contract is not with a customer yet. */
  ownerTag(row: ContractRow): string | null {
    if (row.customerId != null) return null;
    if (row.leadId != null) return 'Lead';
    return row.rollOffOpportunityId != null ? 'Roll-off deal' : null;
  }

  openOwner(row: ContractRow): void {
    if (row.customerId != null) {
      this.router.navigate(['/customers', row.customerId], { queryParams: { tab: 'contracts' } });
    } else if (row.leadId != null) {
      this.router.navigate(['/leads/table', row.leadId]);
    } else if (row.rollOffOpportunityId != null) {
      this.router.navigate(['/rolloff-hires', row.rollOffOpportunityId]);
    }
  }

  /**
   * The files stored for one contract - the generated PDF, the signed copy,
   * the signing certificate: open them, save them, add the signed copy.
   */
  openDocuments(row: ContractRow): void {
    this.dialog.open<ContractDocumentsDialogComponent, ContractDocumentsData>(
      ContractDocumentsDialogComponent,
      {
        width: '720px',
        maxWidth: '95vw',
        autoFocus: false,
        data: {
          contractId: row.id,
          contractLabel: row.contractNo || row.title || `Contract #${row.id}`,
          ownerName: this.ownerName(row),
        },
      });
  }

  noExpiry(row: ContractRow): boolean {
    return row.effectiveTo?.startsWith(NO_EXPIRY) ?? false;
  }

  billingMethodLabel(code: string): string {
    return CONTRACT_BILLING_METHOD_LABELS[code] ?? code;
  }

  modificationLabels(row: ContractRow): string {
    return (row.allowedModificationTypes ?? [])
      .map(type => CONTRACT_MODIFICATION_LABELS[type] ?? type)
      .join(', ');
  }

  contractStatusClass(code: string): string {
    return code === 'Draft' ? 'chip-pending' : 'chip-blue';
  }
}
